<script setup>
import { ref, computed, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { planningApi } from '@/apis/planning.api'
import { KPI_CHART_TYPE_ENUM, getPeriodLabel } from '@/composables/dashboard-kpi'
import { useDashboardStore } from '@/stores/dashboard.store'
import DashboardTile from '@/components/dashboard/DashboardTile.vue'
import KpiMetricTile from '@/components/dashboard/charts/KpiMetricTile.vue'
import KpiChartTile from '@/components/dashboard/charts/KpiChartTile.vue'
import TemplateTile from '@/components/dashboard/TemplateTile.vue'
import TileFiltersStep from '@/components/dashboard/TileFiltersStep.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'


const CHART_TYPE_OPTS = [
  { type: KPI_CHART_TYPE_ENUM.LINE,   labelKey: 'dashboard.kpiChartLine',   icon: 'mdi-chart-line' },
  { type: KPI_CHART_TYPE_ENUM.SPLINE, labelKey: 'dashboard.kpiChartSpline', icon: 'mdi-chart-bell-curve' },
  { type: KPI_CHART_TYPE_ENUM.BAR,    labelKey: 'dashboard.kpiChartBar',    icon: 'mdi-chart-bar' },
]

const DISPLAY_OPTS = [
  { type: 'kpi',   labelKey: 'dashboard.addTileDisplayKpi',   captionKey: 'dashboard.addTileDisplayKpiCaption',   icon: 'mdi-numeric-9-box-outline' },
  { type: 'chart', labelKey: 'dashboard.addTileDisplayChart', captionKey: 'dashboard.addTileDisplayChartCaption', icon: 'mdi-chart-line' },
]

const KIND_OPTS = [
  { kind: 'template', labelKey: 'dashboard.addTileTemplate', captionKey: 'dashboard.addTileTemplateCaption', icon: 'mdi-view-dashboard' },
  { kind: 'custom',   labelKey: 'dashboard.addTileCustom',   captionKey: 'dashboard.addTileCustomCaption',   icon: 'mdi-tune' },
]

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  editTile: { type: Object, default: null },
})
const emit = defineEmits(['update:model-value', 'add', 'update'])

const isEdit = computed(() => !!props.editTile)
const lockDisplay = computed(() => !!props.editTile?.lockDisplay)

const { isMobile } = useDetectDevice()
const { t } = getI18n()
const dashboardStore = useDashboardStore()

const warehouses = ref([])
planningApi.getWarehousesAsync({ all: true }).then(r => { warehouses.value = r.data })

const complexChartOptions = computed(() => dashboardStore.complexCharts.map(c => ({ id: c.id, label: t(c.titleKey), icon: c.icon })))
const kpiMetricsMap = computed(() => dashboardStore.kpiMetricsMap)

// 4 steps: kind → configure → filters → title
const STEP_ORDER = ['kind', 'configure', 'filters', 'title']
const editStep = ref('kind')

const tileKind = ref(null)
const selectedSource = ref(null)
const customTitle = ref('')
const displayType = ref('kpi')
const chosenChartType = ref(KPI_CHART_TYPE_ENUM.LINE)
const pendingWarehouseIds = ref([])
const pendingPeriodType = ref('month')
const pendingPeriodFrom = ref(null)
const pendingPeriodTo = ref(null)

const isTemplate = computed(() => tileKind.value === 'template')
const stepDone = (name) => STEP_ORDER.indexOf(name) < STEP_ORDER.indexOf(editStep.value)

const selectKind = (kind) => {
  tileKind.value = kind
  selectedSource.value = null
  customTitle.value = ''
}

const setSource = (kind, id) => {
  selectedSource.value = { kind, id }
  if (kind === 'metric') {
    customTitle.value = t(dashboardStore.kpiMetric(id).titleKey)
  } else {
    customTitle.value = t(dashboardStore.complexCharts.find(c => c.id === id)?.titleKey ?? '')
  }
}

const selectedSourceSupportsWarehouse = computed(() => {
  if (!selectedSource.value) return true
  if (selectedSource.value.kind === 'metric') return !!(dashboardStore.kpiMetric(selectedSource.value.id)?.supportsWarehouseFilter)
  return !!(dashboardStore.complexCharts.find(c => c.id === selectedSource.value.id)?.supportsWarehouseFilter)
})

const selectedSourceSupportsPeriod = computed(() => {
  if (!selectedSource.value) return true
  if (selectedSource.value.kind === 'metric') return dashboardStore.kpiMetric(selectedSource.value.id)?.supportsPeriodFilter !== false
  return dashboardStore.complexCharts.find(c => c.id === selectedSource.value.id)?.supportsPeriodFilter !== false
})

const selectedSourceHasAnyFilter = computed(() => selectedSourceSupportsWarehouse.value || selectedSourceSupportsPeriod.value)

const handleBack = () => {
  if (editStep.value === 'kind') { close(); return }
  if (editStep.value === 'configure') { if (isEdit.value) { close(); return } editStep.value = 'kind'; return }
  if (editStep.value === 'filters') { editStep.value = 'configure'; return }
  if (editStep.value === 'title') { editStep.value = selectedSourceHasAnyFilter.value ? 'filters' : 'configure'; return }
  close()
}

const confirm = () => {
  const isMetric = selectedSource.value?.kind === 'metric'
  const type = isMetric ? displayType.value : selectedSource.value.id
  const metric = isMetric ? selectedSource.value.id : null
  const chartType = (isMetric && displayType.value === 'chart') ? chosenChartType.value : null
  const payload = {
    type, metric, chartType,
    customTitle: customTitle.value.trim(),
    warehouseIds: pendingWarehouseIds.value,
    periodType: pendingPeriodType.value,
    periodFrom: pendingPeriodFrom.value,
    periodTo: pendingPeriodTo.value,
  }
  if (isEdit.value) {
    emit('update', { id: props.editTile.id, ...payload })
  } else {
    const defaultW = isMetric ? (displayType.value === 'chart' ? 6 : 4) : 6
    const defaultH = isMetric ? (displayType.value === 'chart' ? 8 : 7) : 6
    emit('add', { id: `custom-${Date.now()}`, ...payload, defaultW, defaultH })
  }
  close()
}

const prefillFromEditTile = (et) => {
  tileKind.value = et.kind
  selectedSource.value = et.source ? { ...et.source } : null
  displayType.value = et.displayType ?? 'kpi'
  chosenChartType.value = et.chartType ?? KPI_CHART_TYPE_ENUM.LINE
  customTitle.value = et.title ?? ''
  pendingWarehouseIds.value = [...(et.warehouseIds ?? [])]
  pendingPeriodType.value = et.periodType ?? 'month'
  pendingPeriodFrom.value = et.periodFrom ?? null
  pendingPeriodTo.value = et.periodTo ?? null
  editStep.value = 'configure'
}

watch(() => props.modelValue, (open) => {
  if (open && props.editTile) prefillFromEditTile(props.editTile)
})

const close = () => {
  emit('update:model-value', false)
  setTimeout(() => {
    editStep.value = 'kind'
    tileKind.value = null
    selectedSource.value = null
    customTitle.value = ''
    displayType.value = 'kpi'
    chosenChartType.value = KPI_CHART_TYPE_ENUM.LINE
    pendingWarehouseIds.value = []
    pendingPeriodType.value = 'month'
    pendingPeriodFrom.value = null
    pendingPeriodTo.value = null
  }, 300)
}

const previewFilters = computed(() => ({
  warehouseIds: pendingWarehouseIds.value,
  periodType: pendingPeriodType.value,
  periodFrom: pendingPeriodFrom.value,
  periodTo: pendingPeriodTo.value,
}))

const previewName = computed(() => {
  const custom = customTitle.value?.trim()
  if (custom) return custom
  if (!selectedSource.value) return ''
  const titleKey = selectedSource.value.kind === 'metric'
    ? dashboardStore.kpiMetric(selectedSource.value.id)?.titleKey
    : dashboardStore.complexCharts.find(c => c.id === selectedSource.value.id)?.titleKey
  return t(titleKey ?? '')
})

const previewTile = computed(() => ({ id: 'custom-preview', title: previewName.value }))
const previewPeriodSuffix = computed(() => getPeriodLabel(previewFilters.value, t))
const previewWarehouseNames = computed(() =>
  previewFilters.value.warehouseIds.map(id => warehouses.value.find(w => w.id === id)?.name).filter(Boolean))
const previewChartConfig = computed(() => ({
  metric: selectedSource.value?.id,
  chartType: chosenChartType.value,
  period: 0,
  ...previewFilters.value,
}))
const isWideTilePreview = computed(() =>
  !(selectedSource.value?.kind === 'metric' && displayType.value !== 'chart'))

watch([selectedSource, displayType, chosenChartType], () => {
  setTimeout(() => window.dispatchEvent(new Event('resize')), 150)
})

const onPreviewResized = () => window.dispatchEvent(new Event('resize'))
</script>

<template>
  <q-dialog
    :model-value="modelValue"
    :full-screen="isMobile"
    :transition-show="isMobile ? 'slide-left' : 'scale'"
    :transition-hide="isMobile ? 'slide-right' : 'scale'"
    class="dialog-lg"
    @update:model-value="close"
  >
    <q-card class="dialog-card-flex" :style="isMobile ? undefined : 'height: 78vh;'">

      <!-- Header -->
      <q-card-section class="row items-center q-pb-none" style="flex-shrink: 0;">
        <q-btn
          flat
          round
          dense
          icon="mdi-arrow-left"
          @click="handleBack"
        />
        <div class="text-h6 q-ml-xs">{{ t(isEdit ? 'common.edit' : 'dashboard.addTile') }}</div>
        <q-space />
        <q-btn
          flat
          round
          dense
          icon="mdi-close"
          @click="close"
        />
      </q-card-section>

      <!-- Two-column body -->
      <div class="row col">

        <!-- Left: step content with stepper -->
        <div class="scroll step-col" :class="isMobile ? 'col-12' : 'col-5'">
          <q-stepper v-model="editStep" flat class="q-py-none full-width">

            <!-- Step 1: Template vs Custom (creation only — the kind is fixed once the tile exists) -->
            <q-step
              v-if="!isEdit"
              name="kind"
              :title="t('dashboard.addTileChooseKind')"
              icon="mdi-view-grid-plus-outline"
              :done="stepDone('kind')"
              :header-nav="stepDone('kind')"
            >
              <div class="column q-gutter-sm">
                <q-card
                  v-for="opt in KIND_OPTS"
                  :key="opt.kind"
                  flat
                  bordered
                  clickable
                  :class="['kind-opt', tileKind === opt.kind ? 'kind-opt--active' : '']"
                  @click="selectKind(opt.kind)"
                >
                  <q-card-section class="row items-center q-pa-md">
                    <q-icon
                      :name="opt.icon"
                      size="1.8rem"
                      :color="tileKind === opt.kind ? 'primary' : 'grey-5'"
                      class="q-mr-md"
                    />
                    <div>
                      <div class="text-subtitle2" :class="tileKind === opt.kind ? 'text-primary' : ''">{{ t(opt.labelKey) }}</div>
                      <div class="text-caption text-grey-6">{{ t(opt.captionKey) }}</div>
                    </div>
                  </q-card-section>
                </q-card>
              </div>
              <stepper-navigation :continue="() => editStep = 'configure'" :disable="!tileKind" />
            </q-step>

            <!-- Step 2: Source selection + display options (progressive disclosure) -->
            <q-step
              name="configure"
              :title="isTemplate ? t('dashboard.addTileCharts') : t('dashboard.addTileMetrics')"
              icon="mdi-tune"
              :done="stepDone('configure')"
              :header-nav="isEdit || stepDone('configure')"
            >
              <!-- Template: chart dropdown (behavior=menu prevents full-screen popup in dialog) -->
              <template v-if="isTemplate">
                <q-select
                  :model-value="selectedSource?.kind === 'chart' ? selectedSource.id : null"
                  :options="complexChartOptions"
                  :label="t('dashboard.addTileCharts')"
                  outlined
                  dense
                  emit-value
                  map-options
                  option-value="id"
                  option-label="label"
                  behavior="menu"
                  @update:model-value="id => setSource('chart', id)"
                >
                  <template #option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section avatar style="min-width: 2.5rem;">
                        <q-icon :name="scope.opt.icon" size="sm" />
                      </q-item-section>
                      <q-item-section>{{ scope.opt.label }}</q-item-section>
                    </q-item>
                  </template>
                  <template #selected-item="scope">
                    <div v-if="scope.opt" class="row items-center no-wrap">
                      <q-icon :name="scope.opt.icon" size="sm" class="q-mr-sm" />
                      <span>{{ scope.opt.label }}</span>
                    </div>
                  </template>
                </q-select>
              </template>

              <!-- Custom: scrollable metric list -->
              <template v-else>
                <q-list dense class="source-list q-mb-sm">
                  <q-item
                    v-for="(m, key) in kpiMetricsMap"
                    :key="key"
                    clickable
                    :active="selectedSource?.id === key"
                    active-class="text-primary"
                    @click="setSource('metric', key)"
                  >
                    <q-item-section>{{ t(m.titleKey) }}</q-item-section>
                    <q-item-section v-if="selectedSource?.id === key" side>
                      <q-icon name="mdi-check" color="primary" size="xs" />
                    </q-item-section>
                  </q-item>
                </q-list>

                <!-- Display type — appears after metric is chosen -->
                <template v-if="selectedSource?.kind === 'metric'">
                  <div class="text-caption text-grey-5 text-uppercase q-mb-xs" style="letter-spacing: 0.06em;">
                    {{ t('dashboard.addTileStep3') }}
                  </div>
                  <div class="row q-gutter-sm q-mb-sm">
                    <q-card
                      v-for="opt in DISPLAY_OPTS"
                      :key="opt.type"
                      flat
                      bordered
                      :clickable="!lockDisplay"
                      :class="['opt-card', displayType === opt.type ? 'opt-card--active' : '', lockDisplay && displayType !== opt.type ? 'opt-card--disabled' : '']"
                      @click="lockDisplay || (displayType = opt.type)"
                    >
                      <q-card-section class="text-center q-pa-sm">
                        <q-icon :name="opt.icon" size="1.5rem" :color="displayType === opt.type ? 'primary' : 'grey-5'" />
                        <div class="text-caption q-mt-xs" :class="displayType === opt.type ? 'text-primary' : ''">{{ t(opt.labelKey) }}</div>
                      </q-card-section>
                    </q-card>
                  </div>

                  <!-- Chart type — appears after chart display is chosen -->
                  <template v-if="displayType === 'chart'">
                    <div class="text-caption text-grey-5 text-uppercase q-mb-xs" style="letter-spacing: 0.06em;">
                      {{ t('dashboard.addTileStep4') }}
                    </div>
                    <div class="row q-gutter-sm q-mb-sm">
                      <q-card
                        v-for="ct in CHART_TYPE_OPTS"
                        :key="ct.type"
                        flat
                        bordered
                        clickable
                        :class="['opt-card', chosenChartType === ct.type ? 'opt-card--active' : '']"
                        @click="chosenChartType = ct.type"
                      >
                        <q-card-section class="text-center q-pa-sm">
                          <q-icon :name="ct.icon" size="1.5rem" :color="chosenChartType === ct.type ? 'primary' : 'grey-5'" />
                          <div class="text-caption q-mt-xs" :class="chosenChartType === ct.type ? 'text-primary' : ''">{{ t(ct.labelKey) }}</div>
                        </q-card-section>
                      </q-card>
                    </div>
                  </template>
                </template>
              </template>

              <stepper-navigation
                :back="handleBack"
                :continue="() => editStep = selectedSourceHasAnyFilter ? 'filters' : 'title'"
                :disable="!selectedSource"
              />
            </q-step>

            <!-- Step 3: Filters -->
            <q-step
              v-if="selectedSourceHasAnyFilter"
              name="filters"
              :title="t('dashboard.addTileFilters')"
              icon="mdi-filter-outline"
              :done="stepDone('filters')"
              :header-nav="isEdit || stepDone('filters')"
            >
              <tile-filters-step
                v-model:warehouse-ids="pendingWarehouseIds"
                v-model:period-type="pendingPeriodType"
                v-model:period-from="pendingPeriodFrom"
                v-model:period-to="pendingPeriodTo"
                :show-warehouse-filter="selectedSourceSupportsWarehouse"
                :show-period-filter="selectedSourceSupportsPeriod"
                :back="() => editStep = 'configure'"
                :next="() => editStep = 'title'"
              />
            </q-step>

            <!-- Step 4: Title -->
            <q-step
              name="title"
              :title="t('dashboard.addTileTitleLabel')"
              icon="mdi-pencil"
              :done="stepDone('title')"
              :header-nav="isEdit || stepDone('title')"
            >
              <q-input
                v-model="customTitle"
                :label="t('dashboard.addTileTitleLabel')"
                outlined
                dense
                autofocus
                class="q-mb-sm"
              />
              <stepper-navigation
                :back="() => editStep = selectedSourceHasAnyFilter ? 'filters' : 'configure'"
                :finish="confirm"
                :finish-label="t('common.confirm')"
                icon-right="mdi-check"
              />
            </q-step>

          </q-stepper>
        </div>

        <!-- Right: live preview — desktop only -->
        <div v-if="!isMobile" class="col-7 preview-col flex flex-center column q-pa-md">

          <template v-if="!selectedSource">
            <q-icon
              name="mdi-view-dashboard-outline"
              size="5rem"
              color="grey-4"
              class="q-mb-md"
            />
            <div class="text-body2 text-grey-5 text-center">{{ t('dashboard.addTilePreviewEmpty') }}</div>
          </template>

          <!-- Faithful preview: same components the tile will render with on the dashboard; drag the corner to resize -->
          <div
            v-else
            class="tile-preview"
            :class="isWideTilePreview ? 'tile-preview--wide' : ''"
            @mouseup="onPreviewResized"
          >
            <dashboard-tile
              :tile="previewTile"
              :title-suffix="previewPeriodSuffix"
              :warehouse-names="previewWarehouseNames"
            >
              <kpi-metric-tile
                v-if="selectedSource.kind === 'metric' && displayType !== 'chart'"
                tile-id="custom-preview"
                :metric-key="selectedSource.id"
                :tile-filters="previewFilters"
                :tile-name="previewName"
                :warehouses="warehouses"
              />
              <kpi-chart-tile
                v-else-if="selectedSource.kind === 'metric' && displayType === 'chart'"
                tile-id="custom-preview"
                :chart-config="previewChartConfig"
                :tile-name="previewName"
              />
              <template-tile
                v-else
                tile-id="custom-preview"
                :tile-type="selectedSource.id"
                :filters="previewFilters"
                :tile-name="previewName"
              />
            </dashboard-tile>
          </div>

        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.step-col {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
.preview-col {
  background: rgba(0, 0, 0, 0.02);
}
.source-list {
  max-height: 14rem;
  overflow-y: auto;
}
.kind-opt {
  cursor: pointer;
  transition: border-color 0.15s;
}
.kind-opt--active {
  border-color: var(--q-primary) !important;
}
.opt-card {
  flex: 1;
  min-width: 5.5rem;
  cursor: pointer;
  transition: border-color 0.15s;
}
.opt-card--active {
  border-color: var(--q-primary) !important;
}
.opt-card--disabled {
  opacity: 0.5;
}
.tile-preview {
  width: 16rem;
  height: 18rem;
  min-width: 10rem;
  min-height: 8rem;
  max-width: 100%;
  resize: both;
  overflow: hidden;
}
.tile-preview--wide {
  width: 28rem;
}
</style>
